import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowLeft, Shield, Mail, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const RefundPolicyPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white py-8">
        <div className="container mx-auto px-4">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{language === 'hi' ? 'वापस जाएं' : 'Go Back'}</span>
          </button>
          <h1 className={`text-3xl md:text-4xl font-black ${language === 'hi' ? 'font-hindi' : ''}`}>
            {language === 'hi' ? 'रिफंड पॉलिसी' : 'Refund Policy'}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-orange-100">
          
          {/* Introduction */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-8 h-8 text-green-600" />
              <h2 className={`text-2xl font-bold text-gray-900 ${language === 'hi' ? 'font-hindi' : ''}`}>
                {language === 'hi' ? '100% संतुष्टि गारंटी' : '100% Satisfaction Guarantee'}
              </h2>
            </div>
            <p className={`text-gray-600 leading-relaxed ${language === 'hi' ? 'font-hindi' : ''}`}>
              {language === 'hi'
                ? 'Karma Kundali में हम आपकी संतुष्टि को सर्वोपरि मानते हैं। हमारी रिफंड पॉलिसी पारदर्शी और ग्राहक-केंद्रित है।'
                : 'At Karma Kundali, we prioritize your satisfaction above all. Our refund policy is transparent and customer-centric.'}
            </p>
          </div>

          {/* Policy Points */}
          <div className="space-y-8">
            <div>
              <h3 className={`text-xl font-bold text-gray-900 mb-3 ${language === 'hi' ? 'font-hindi' : ''}`}>
                {language === 'hi' ? '1. रिफंड की पात्रता' : '1. Refund Eligibility'}
              </h3>
              <ul className={`list-disc list-inside space-y-2 text-gray-600 ${language === 'hi' ? 'font-hindi' : ''}`}>
                <li>{language === 'hi' ? 'यदि आपको 48 घंटे के भीतर कुंडली रिपोर्ट नहीं मिलती है, तो आप पूर्ण रिफंड के हकदार हैं।' : 'If you do not receive the Kundli report within 48 hours, you are entitled to a full refund.'}</li>
                <li>{language === 'hi' ? 'यदि रिपोर्ट में आपका जन्म विवरण गलत है (हमारी गलती से), तो हम मुफ्त में सही रिपोर्ट देंगे या रिफंड करेंगे।' : 'If the report has incorrect birth details (due to our mistake), we will provide a correct report free of charge or issue a refund.'}</li>
                <li>{language === 'hi' ? 'पेमेंट के 24 घंटे के भीतर रिफंड अनुरोध किया जा सकता है, बशर्ते रिपोर्ट अभी तक नहीं भेजी गई हो।' : 'Refund can be requested within 24 hours of payment, provided the report has not been sent yet.'}</li>
              </ul>
            </div>

            <div>
              <h3 className={`text-xl font-bold text-gray-900 mb-3 ${language === 'hi' ? 'font-hindi' : ''}`}>
                {language === 'hi' ? '2. रिफंड की प्रक्रिया' : '2. Refund Process'}
              </h3>
              <ul className={`list-disc list-inside space-y-2 text-gray-600 ${language === 'hi' ? 'font-hindi' : ''}`}>
                <li>{language === 'hi' ? 'रिफंड के लिए karmakundali2@gmail.com पर ईमेल करें।' : 'Email karmakundali2@gmail.com for refund requests.'}</li>
                <li>{language === 'hi' ? 'अपना ऑर्डर आईडी और पेमेंट विवरण साझा करें।' : 'Share your order ID and payment details.'}</li>
                <li>{language === 'hi' ? 'रिफंड 5-7 कार्य दिवसों में आपके मूल भुगतान माध्यम में वापस कर दिया जाएगा।' : 'Refund will be credited to your original payment method within 5-7 business days.'}</li>
              </ul>
            </div>

            <div>
              <h3 className={`text-xl font-bold text-gray-900 mb-3 ${language === 'hi' ? 'font-hindi' : ''}`}>
                {language === 'hi' ? '3. रिफंड अस्वीकृति' : '3. Non-Refundable Cases'}
              </h3>
              <ul className={`list-disc list-inside space-y-2 text-gray-600 ${language === 'hi' ? 'font-hindi' : ''}`}>
                <li>{language === 'hi' ? 'रिपोर्ट डिलीवर होने के बाद रिफंड नहीं मिलेगा।' : 'No refund after the report has been delivered.'}</li>
                <li>{language === 'hi' ? 'यदि आपने गलत जन्म विवरण दिया है, तो रिफंड नहीं होगा।' : 'No refund if you have provided incorrect birth details.'}</li>
                <li>{language === 'hi' ? 'भविष्यवाणियों से असंतुष्टि रिफंड का कारण नहीं है।' : 'Dissatisfaction with predictions is not grounds for refund.'}</li>
              </ul>
            </div>

            <div>
              <h3 className={`text-xl font-bold text-gray-900 mb-3 ${language === 'hi' ? 'font-hindi' : ''}`}>
                {language === 'hi' ? '4. संपर्क करें' : '4. Contact Us'}
              </h3>
              <div className="bg-orange-50 rounded-xl p-6 border border-orange-200">
                <p className={`text-gray-700 mb-4 ${language === 'hi' ? 'font-hindi' : ''}`}>
                  {language === 'hi'
                    ? 'किसी भी प्रश्न के लिए हमसे संपर्क करें:'
                    : 'Contact us for any questions:'}
                </p>
                <div className="space-y-2">
                  <a href="mailto:karmakundali2@gmail.com" className="flex items-center gap-2 text-orange-600 hover:text-orange-700">
                    <Mail className="w-5 h-5" />
                    <span>karmakundali2@gmail.com</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Last Updated */}
          <div className="mt-10 pt-6 border-t border-gray-200">
            <p className="text-gray-500 text-sm">
              {language === 'hi' ? 'अंतिम अपडेट: फरवरी 2026' : 'Last Updated: February 2026'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicyPage;
